// ============================================================
// 작성 폼 chrome(헤더·하단 액션바) 구성 편집 — composeChrome (Phase 3 Part B)
// ============================================================
//
// template.composeChrome(ComposeChromeConfig)를 편집한다. 미지정이면 작성 페이지(new-composed.tsx)가
// DEFAULT_COMPOSE_CHROME(기본 세트: 헤더 뒤로+제목, 하단 제출·미리보기·취소)로 렌더한다 — 회귀 안전.
// 이 에디터는 "커스터마이즈" 시에만 config를 materialize해 헤더 토글·버튼 항목을 편집한다.
// 결정: docs/decisions/2026-07-09-compose-chrome-config.md

import { useCallback } from 'react';
import type {
  ComposeChromeConfig,
  ComposeChromeItem,
  ComposeChromeItemViewOverride,
  ComposeChromeActionId,
  ComposeChromeContentWidth,
  ComposeChromeDisplay,
  ComposeChromeSlot,
} from '@safetyhub/shared';
import {
  COMPOSE_CHROME_ACTION_LABELS,
  COMPOSE_CHROME_CONTENT_WIDTH_LABELS,
  COMPOSE_CHROME_CONTENT_WIDTH_PX,
  COMPOSER_VIEWS,
  DEFAULT_COMPOSE_CHROME,
  DEFAULT_COMPOSE_CHROME_CONTENT_WIDTH,
} from '@safetyhub/shared';
import { Button, Card } from '@safetyhub/ui';

/** cc- 네임스페이스 고유 id 생성. crypto.randomUUID 우선, 폴백은 시간+랜덤. */
function makeChromeItemId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `cc-${uuid}`;
  return `cc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/** 항목 종류(내장 액션 vs 커스텀 링크) 세그먼트 옵션 */
const KIND_OPTIONS: { value: 'action' | 'link'; label: string }[] = [
  { value: 'action', label: '내장 액션' },
  { value: 'link', label: '링크' },
];

/** 표시형(뷰 오버라이드) 옵션 — 상속(undefined)/텍스트/아이콘/아이콘+텍스트 */
const DISPLAY_OPTIONS: { value: ComposeChromeDisplay; label: string }[] = [
  { value: 'label', label: '텍스트' },
  { value: 'icon', label: '아이콘' },
  { value: 'iconLabel', label: '아이콘+텍스트' },
];

/** 슬롯 세그먼트 옵션 */
const SLOT_OPTIONS: { value: ComposeChromeSlot; label: string }[] = [
  { value: 'header', label: '헤더' },
  { value: 'actionBar', label: '하단 액션바' },
];

/** 노출 시점(모드) 세그먼트 옵션 — 'both'=공통(visibleIn 미지정). */
const VISIBLE_IN_OPTIONS: { value: 'both' | 'create' | 'edit'; label: string }[] = [
  { value: 'both', label: '공통' },
  { value: 'create', label: '생성만' },
  { value: 'edit', label: '수정만' },
];

/** 본문 폭 select 옵션(좁게 672 / 보통 896 / 넓게 1152) — 라벨은 상수 + px 병기. */
const CONTENT_WIDTH_OPTIONS: { value: ComposeChromeContentWidth; label: string }[] = (
  ['narrow', 'normal', 'wide'] as ComposeChromeContentWidth[]
).map((w) => ({
  value: w,
  label: `${COMPOSE_CHROME_CONTENT_WIDTH_LABELS[w]} ${COMPOSE_CHROME_CONTENT_WIDTH_PX[w]}px`,
}));

/** 내장 액션 select 옵션(라벨은 COMPOSE_CHROME_ACTION_LABELS). */
const ACTION_OPTIONS: { value: ComposeChromeActionId; label: string }[] = (
  Object.keys(COMPOSE_CHROME_ACTION_LABELS) as ComposeChromeActionId[]
).map((id) => ({ value: id, label: COMPOSE_CHROME_ACTION_LABELS[id] }));

/**
 * 세그먼트 토글(작성폼 편집 뷰 표시 모드 토글 계열 공통 스타일).
 */
function Segment<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded border border-gray-300 overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-2 py-1 text-xs whitespace-nowrap border-l border-gray-300 first:border-l-0 ${
            value === opt.value
              ? 'bg-brand-500 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
          aria-pressed={value === opt.value}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** 오버라이드된 속성 배지(결정 §5). */
function OverrideBadge() {
  return (
    <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700">
      재정의
    </span>
  );
}

/**
 * 한 항목의 뷰별 오버라이드 편집 — base가 아닌 뷰마다 한 줄(상속↔오버라이드 토글 + hidden/slot/order).
 * COMPOSER_VIEWS(isBase 아닌 것)를 순회 — 하드코딩 뷰 금지(레지스트리 확장 자동 수용).
 */
function ViewOverridesEditor({
  item,
  onPatch,
}: {
  item: ComposeChromeItem;
  onPatch: (
    viewKey: string,
    patch: Partial<ComposeChromeItemViewOverride> | null,
  ) => void;
}) {
  const nonBaseViews = COMPOSER_VIEWS.filter((v) => !v.isBase);
  if (nonBaseViews.length === 0) return null;
  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <span className="mb-1.5 block text-[11px] font-medium text-gray-500">뷰별 조정</span>
      <div className="space-y-2">
        {nonBaseViews.map((view) => {
          const ov = item.responsive?.[view.key];
          const enabled = ov !== undefined;
          return (
            <div
              key={view.key}
              className="rounded border border-gray-200 bg-white px-2.5 py-2 text-xs text-gray-600"
            >
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => onPatch(view.key, e.target.checked ? {} : null)}
                />
                <span className="font-medium">{view.label}</span>
                {enabled ? (
                  <span className="text-gray-400">오버라이드</span>
                ) : (
                  <span className="text-gray-400">상속</span>
                )}
              </label>
              {enabled && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 pl-5">
                  {/* hidden */}
                  <label className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={ov?.hidden ?? false}
                      onChange={(e) =>
                        onPatch(view.key, { hidden: e.target.checked || undefined })
                      }
                    />
                    <span>숨김</span>
                    {ov?.hidden !== undefined && <OverrideBadge />}
                  </label>
                  {/* slot */}
                  <div className="flex items-center gap-1.5">
                    <span>슬롯</span>
                    <select
                      value={ov?.slot ?? ''}
                      onChange={(e) =>
                        onPatch(view.key, {
                          slot: (e.target.value || undefined) as
                            | ComposeChromeSlot
                            | undefined,
                        })
                      }
                      className="rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
                    >
                      <option value="">상속</option>
                      {SLOT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {ov?.slot !== undefined && <OverrideBadge />}
                  </div>
                  {/* order */}
                  <div className="flex items-center gap-1.5">
                    <span>순서</span>
                    <input
                      type="number"
                      value={ov?.order ?? ''}
                      onChange={(e) =>
                        onPatch(view.key, {
                          order:
                            e.target.value === ''
                              ? undefined
                              : Number(e.target.value),
                        })
                      }
                      placeholder="상속"
                      className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                    {ov?.order !== undefined && <OverrideBadge />}
                  </div>
                  {/* display(표시형) */}
                  <div className="flex items-center gap-1.5">
                    <span>표시형</span>
                    <select
                      value={ov?.display ?? ''}
                      onChange={(e) =>
                        onPatch(view.key, {
                          display: (e.target.value || undefined) as
                            | ComposeChromeDisplay
                            | undefined,
                        })
                      }
                      className="rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
                    >
                      <option value="">상속</option>
                      {DISPLAY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {ov?.display !== undefined && <OverrideBadge />}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * composeChrome 편집 — 미구성(기본 세트) 안내 + 커스터마이즈 진입 / 헤더 토글 + 항목 편집.
 *
 * @param config   현재 template.composeChrome(미지정이면 기본 세트 사용 중)
 * @param onChange 갱신된 ComposeChromeConfig(불변) — 상위 apply funnel로 undo/isDirty 편입
 */
export function ComposeChromeEditor({
  config,
  onChange,
  slot,
}: {
  config?: ComposeChromeConfig;
  onChange: (next: ComposeChromeConfig) => void;
  /** 지정 시 그 슬롯(header/actionBar) 항목만 스코프 편집(선택→속성창 모델). 미지정=전체. */
  slot?: ComposeChromeSlot;
}) {
  // config 미지정(=기본 세트 사용 중): 커스터마이즈 진입 안내만 표시.
  const materialize = useCallback(
    () => onChange(structuredClone(DEFAULT_COMPOSE_CHROME)),
    [onChange],
  );

  const patchItem = useCallback(
    (id: string, patch: Partial<ComposeChromeItem>) => {
      if (!config) return;
      onChange({
        ...config,
        items: config.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
      });
    },
    [config, onChange],
  );

  const removeItem = useCallback(
    (id: string) => {
      if (!config) return;
      onChange({ ...config, items: config.items.filter((it) => it.id !== id) });
    },
    [config, onChange],
  );

  // 항목의 뷰별 오버라이드(responsive[viewKey]) 갱신. patch=null이면 그 뷰 델타 제거.
  // 빈 객체({}: 모든 속성 상속)는 "오버라이드 on(상속 중)" 상태로 유지, responsive 자체가 비면 제거.
  const patchViewOverride = useCallback(
    (
      id: string,
      viewKey: string,
      patch: Partial<ComposeChromeItemViewOverride> | null,
    ) => {
      if (!config) return;
      const target = config.items.find((it) => it.id === id);
      if (!target) return;
      const nextResponsive: Record<string, ComposeChromeItemViewOverride> = {
        ...target.responsive,
      };
      if (patch === null) {
        delete nextResponsive[viewKey];
      } else {
        const merged: ComposeChromeItemViewOverride = {
          ...nextResponsive[viewKey],
          ...patch,
        };
        // undefined로 지정된 속성은 상속으로 되돌리기 위해 키 제거.
        (Object.keys(patch) as (keyof ComposeChromeItemViewOverride)[]).forEach((k) => {
          if (patch[k] === undefined) delete merged[k];
        });
        nextResponsive[viewKey] = merged;
      }
      const hasAny = Object.keys(nextResponsive).length > 0;
      patchItem(id, { responsive: hasAny ? nextResponsive : undefined });
    },
    [config, patchItem],
  );

  const addItem = useCallback(() => {
    if (!config) return;
    // 스코프 슬롯(미지정=actionBar) 내 말단 order 부여.
    const targetSlot: ComposeChromeSlot = slot ?? 'actionBar';
    const maxOrder = config.items
      .filter((it) => it.slot === targetSlot)
      .reduce((m, it) => Math.max(m, it.order), -1);
    const next: ComposeChromeItem = {
      id: makeChromeItemId(),
      actionId: 'submit',
      slot: targetSlot,
      order: maxOrder + 1,
    };
    onChange({ ...config, items: [...config.items, next] });
  }, [config, onChange, slot]);

  // 같은 슬롯 내 order 교환(▲▼) — dir=-1(위)/+1(아래). 정렬은 slot 필터 후 order 오름차순 기준.
  const moveItem = useCallback(
    (id: string, dir: -1 | 1) => {
      if (!config) return;
      const target = config.items.find((it) => it.id === id);
      if (!target) return;
      const sameSlot = config.items
        .filter((it) => it.slot === target.slot)
        .sort((a, b) => a.order - b.order);
      const idx = sameSlot.findIndex((it) => it.id === id);
      const swapWith = sameSlot[idx + dir];
      if (!swapWith) return; // 경계
      // order 값 교환.
      onChange({
        ...config,
        items: config.items.map((it) => {
          if (it.id === target.id) return { ...it, order: swapWith.order };
          if (it.id === swapWith.id) return { ...it, order: target.order };
          return it;
        }),
      });
    },
    [config, onChange],
  );

  const reset = useCallback(
    () => onChange(structuredClone(DEFAULT_COMPOSE_CHROME)),
    [onChange],
  );

  // 슬롯 스코프에 따른 제목(선택→속성창 모델). 미지정=전체 구성.
  const title =
    slot === 'header'
      ? '헤더 버튼 구성'
      : slot === 'actionBar'
        ? '하단 컨트롤 바 버튼 구성'
        : '작성 폼 버튼 구성';

  if (!config) {
    return (
      <Card elevation="none" className="shrink-0">
        <h3 className="mb-2 text-sm font-semibold text-gray-700">{title}</h3>
        <div className="rounded-card border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center text-xs text-gray-500">
          <p className="mb-2">기본 세트 사용 중 (제출 · 미리보기 · 취소)</p>
          <Button variant="primary" size="xs" onClick={materialize}>
            커스터마이즈
          </Button>
        </div>
      </Card>
    );
  }

  // 정렬 표시: 슬롯별 → order 오름차순. slot 스코프 지정 시 그 슬롯 항목만.
  const sortedItems = [...config.items]
    .filter((it) => (slot ? it.slot === slot : true))
    .sort(
      (a, b) =>
        (a.slot === b.slot ? 0 : a.slot === 'header' ? -1 : 1) || a.order - b.order,
    );

  return (
    <Card elevation="none" className="shrink-0">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="xs" onClick={reset}>
            기본값으로 초기화
          </Button>
          <Button variant="primary" size="xs" onClick={addItem}>
            + 항목 추가
          </Button>
        </div>
      </div>
      <p className="mb-3 text-xs text-gray-500">
        작성 페이지 헤더/하단 액션바에 표시할 버튼을 구성합니다. (미구성 시 기본 세트 렌더)
      </p>

      {/* 헤더 토글 — 헤더 스코프(또는 전체)에서만 표시(하단 바 스코프엔 무관). */}
      {slot !== 'actionBar' && (
        <Card
          padding="none"
          surface="soft"
          elevation="none"
          className="mb-3 flex flex-wrap gap-4 px-3 py-2"
        >
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={config.showBack ?? true}
              onChange={(e) => onChange({ ...config, showBack: e.target.checked })}
            />
            <span>뒤로가기(←) 표시</span>
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={config.showTitle ?? true}
              onChange={(e) => onChange({ ...config, showTitle: e.target.checked })}
            />
            <span>제목 표시</span>
          </label>
        </Card>
      )}

      {sortedItems.length === 0 ? (
        <div className="rounded-card border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center text-xs text-gray-400">
          버튼 항목이 없습니다. 우측 상단 [항목 추가]로 만드세요.
        </div>
      ) : (
        <ul className="space-y-3">
          {sortedItems.map((item) => {
            const kind: 'action' | 'link' = item.link ? 'link' : 'action';
            return (
              <li
                key={item.id}
                className="rounded-card border border-gray-200 bg-gray-50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-[11px] text-gray-400">{item.id}</span>
                  <div className="flex items-center gap-1">
                    {/* 같은 슬롯 내 순서 이동 */}
                    <button
                      type="button"
                      onClick={() => moveItem(item.id, -1)}
                      className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
                      title="위로"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(item.id, 1)}
                      className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
                      title="아래로"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="rounded border border-red-200 bg-white px-2 py-0.5 text-xs text-red-600 hover:bg-red-50"
                    >
                      삭제
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* 종류(내장/링크) */}
                  <div className="flex flex-col gap-1 text-xs text-gray-600">
                    <span className="font-medium">종류</span>
                    <Segment
                      value={kind}
                      options={KIND_OPTIONS}
                      onChange={(v) => {
                        // 종류 전환 시 actionId/link 상호배타로 리셋.
                        if (v === 'action') {
                          patchItem(item.id, { actionId: 'submit', link: undefined });
                        } else {
                          patchItem(item.id, {
                            actionId: undefined,
                            link: { kind: 'link', href: '' },
                          });
                        }
                      }}
                    />
                  </div>

                  {/* 슬롯 */}
                  <div className="flex flex-col gap-1 text-xs text-gray-600">
                    <span className="font-medium">배치 슬롯</span>
                    <Segment
                      value={item.slot}
                      options={SLOT_OPTIONS}
                      onChange={(v) => patchItem(item.id, { slot: v })}
                    />
                  </div>

                  {/* 종류별: 내장 액션 select 또는 링크 href */}
                  {kind === 'action' ? (
                    <label className="flex flex-col gap-1 text-xs text-gray-600">
                      <span className="font-medium">액션</span>
                      <select
                        value={item.actionId ?? 'submit'}
                        onChange={(e) =>
                          patchItem(item.id, {
                            actionId: e.target.value as ComposeChromeActionId,
                          })
                        }
                        className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                      >
                        {ACTION_OPTIONS.map((a) => (
                          <option key={a.value} value={a.value}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label className="flex flex-col gap-1 text-xs text-gray-600">
                      <span className="font-medium">링크 URL</span>
                      <input
                        type="text"
                        value={item.link?.href ?? ''}
                        onChange={(e) =>
                          patchItem(item.id, {
                            link: { kind: 'link', href: e.target.value },
                          })
                        }
                        placeholder="https://... 또는 /경로"
                        className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                      />
                    </label>
                  )}

                  {/* 라벨(override) */}
                  <label className="flex flex-col gap-1 text-xs text-gray-600">
                    <span className="font-medium">라벨 (선택)</span>
                    <input
                      type="text"
                      value={item.label ?? ''}
                      onChange={(e) =>
                        patchItem(item.id, { label: e.target.value || undefined })
                      }
                      placeholder={
                        item.actionId
                          ? COMPOSE_CHROME_ACTION_LABELS[item.actionId]
                          : '버튼 라벨'
                      }
                      className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                  </label>

                  {/* primary */}
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={item.primary ?? false}
                      onChange={(e) =>
                        patchItem(item.id, { primary: e.target.checked || undefined })
                      }
                    />
                    <span className="font-medium">강조(primary) 버튼</span>
                  </label>

                  {/* 노출 시점(모드) — 공통/생성만/수정만. 'both'=visibleIn 미지정(공통). */}
                  <div className="flex flex-col gap-1 text-xs text-gray-600">
                    <span className="font-medium">노출 시점</span>
                    <Segment
                      value={item.visibleIn ?? 'both'}
                      options={VISIBLE_IN_OPTIONS}
                      onChange={(v) =>
                        patchItem(item.id, { visibleIn: v === 'both' ? undefined : v })
                      }
                    />
                  </div>
                </div>

                {/* 뷰별 조정(반응형 오버라이드) */}
                <ViewOverridesEditor
                  item={item}
                  onPatch={(viewKey, patch) => patchViewOverride(item.id, viewKey, patch)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/**
 * 작성 페이지 전역 설정("페이지 설정") — 위젯/크롬 미선택 시 속성 패널에 표시(보고서 탭의
 * 전역 설정 모델과 동일). 본문 폭 등 페이지 틀 속성은 특정 슬롯(헤더) 소속이 아니라 여기서 편집.
 * config 미구성(기본 세트) 상태에서도 바로 편집 가능 — 변경 시 기본 세트를 materialize 후 반영
 * (버튼 구성의 "커스터마이즈" 진입과 동일 패턴).
 */
export function ComposePageSettingsEditor({
  config,
  onChange,
}: {
  config?: ComposeChromeConfig;
  onChange: (next: ComposeChromeConfig) => void;
}) {
  return (
    <Card elevation="none" className="shrink-0">
      <h3 className="mb-2 text-sm font-semibold text-gray-700">페이지 설정</h3>
      {/* 본문 폭 — 콘텐츠 컬럼 최대 폭(상한). 미지정=넓게(wide). */}
      <label className="flex items-center justify-between gap-2 text-xs text-gray-600">
        <span>본문 폭</span>
        <select
          value={config?.contentWidth ?? DEFAULT_COMPOSE_CHROME_CONTENT_WIDTH}
          onChange={(e) =>
            onChange({
              ...(config ?? structuredClone(DEFAULT_COMPOSE_CHROME)),
              contentWidth: e.target.value as ComposeChromeContentWidth,
            })
          }
          className="rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
        >
          {CONTENT_WIDTH_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <p className="mt-1.5 text-[11px] leading-snug text-gray-400">
        본문 최대 폭 상한 — 좁은 화면에서는 설정과 무관하게 전체 폭을 사용합니다.
      </p>
    </Card>
  );
}
